import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Bot, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to convert email addresses to clickable links
const formatMessageWithLinks = (text) => {
  // Match email addresses
  const emailRegex = /([a-zA-Z0-9._-]+@kiddiplayclub\.com)/g;
  const parts = text.split(emailRegex);
  
  return parts.map((part, i) => {
    if (part.match(emailRegex)) {
      return (
        <a 
          key={i} 
          href={`mailto:${part}`} 
          className="text-purple-600 hover:text-purple-800 underline font-medium"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      content: "Hi! 👋 I'm KiddiPlay's assistant. Ask me about matching, Buddy UP!, safety, subscriptions, or SEN support! For direct help, email team@kiddiplayclub.com",
      source: "initial"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/chatbot`,
        { message: userMessage },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      
      setMessages(prev => [...prev, { 
        role: "bot", 
        content: response.data.response,
        source: response.data.source
      }]);
    } catch (error) {
      // If not authenticated, show helpful message
      if (error.response?.status === 401) {
        setMessages(prev => [...prev, { 
          role: "bot", 
          content: "Please log in to chat with our AI assistant! Here are some quick answers:\n\n• **Premium**: £9.99/month with unlimited matches\n• **Buddy UP!**: Split costs on tutoring, babysitting & more\n• **Safety**: Verified users, check-ins, moderated chat\n• **Contact**: team@kiddiplayclub.com\n\nLog in for personalized help!",
          source: "fallback"
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: "bot", 
          content: "I'm having trouble right now. Please email team@kiddiplayclub.com and we'll help you directly!",
          source: "error"
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    "What is Buddy UP!?",
    "How does matching work?",
    "Is my data safe?",
    "Premium features",
    "SEN support"
  ];

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 hero-gradient rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-50"
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="hero-gradient p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">KiddiPlay Assistant</h3>
                <p className="text-white/70 text-xs">Here to help!</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === "user"
                      ? "bg-purple-500 text-white rounded-br-md"
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">
                    {msg.role === "bot" ? formatMessageWithLinks(msg.content) : msg.content}
                  </p>
                  {msg.source === "ai" && (
                    <p className="text-xs mt-1 opacity-60">AI-powered response</p>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl px-4 py-3 rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="text-xs bg-purple-50 text-purple-600 px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-slate-100">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 rounded-full"
                disabled={loading}
                data-testid="chatbot-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-10 h-10 rounded-full hero-gradient p-0"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4 text-white" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
